import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { MonitorCatalog } from "@/routes/base-data/processes/qpm/catalog/[catalogId]/_components/monitor-catalog-table-component";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  DEFAULT_MONITOR_TYPE_QUERY,
  monitorTypeFromQuery,
  monitorTypeToQuery,
} from "@/routes/base-data/processes/qpm/catalog/monitor-type-query";
import { CatalogMonitorAdditionalsLibraryTable } from "@/routes/base-data/processes/qpm/catalog/[catalogId]/[monitorId]/_components/catalog-monitor-additionals-library-table";

export function CatalogMonitorsListCard({ catalogId }: { catalogId: string }) {
  const location = useLocation();
  const navigate = useNavigate();

  const { activeTypeQuery, activeMonitorType } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("type");
    const parsed = monitorTypeFromQuery(raw);

    const query = parsed
      ? monitorTypeToQuery(parsed)
      : DEFAULT_MONITOR_TYPE_QUERY;

    return {
      activeTypeQuery: query,
      activeMonitorType: parsed ?? MonitorType.IoT,
    };
  }, [location.search]);

  // If there's no/invalid query, normalize the URL to the default so deep-links are shareable.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = (params.get("type") ?? "").trim().toLowerCase();
    if (current !== activeTypeQuery) {
      params.set("type", activeTypeQuery);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
    // Intentionally omit navigate/location.pathname from deps to avoid loops; we only react to search changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTypeQuery, location.search]);

  const onTypeChange = (nextType: string) => {
    const params = new URLSearchParams(location.search);
    params.set("type", nextType);

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  return (
    <div>
      <CardHeader>
        <div className="flex items-end justify-between">
          <CardTitle>{t("Monitore")}</CardTitle>
        </div>
        <CardDescription>
          {t(
            "Sie können hier neue Monitore anlegen oder bestehende bearbeiten.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs
          value={activeTypeQuery}
          onValueChange={onTypeChange}
          className="mt-2"
        >
          <TabsList>
            <TabsTrigger value="checkpoint">
              {MonitorTypeUtil.toLongString(MonitorType.Checkpoint)}
            </TabsTrigger>
            <TabsTrigger value="flyex">
              {MonitorTypeUtil.toLongString(MonitorType.FlyExterminator)}
            </TabsTrigger>
            <TabsTrigger value="iot">
              {MonitorTypeUtil.toLongString(MonitorType.IoT)}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="checkpoint">
            <MonitorCatalog
              catalogId={catalogId}
              monitorTypeKey={MonitorType.Checkpoint}
            />
          </TabsContent>
          <TabsContent value="flyex">
            <MonitorCatalog
              catalogId={catalogId}
              monitorTypeKey={MonitorType.FlyExterminator}
            />
          </TabsContent>
          <TabsContent value="iot">
            <MonitorCatalog
              catalogId={catalogId}
              monitorTypeKey={MonitorType.IoT}
            />
          </TabsContent>
        </Tabs>
        <div className="mt-6">
          <CatalogMonitorAdditionalsLibraryTable
            catalogId={catalogId}
            monitorType={activeMonitorType}
          />
        </div>
      </CardContent>
    </div>
  );
}

/** @deprecated Use CatalogMonitorsListCard */
export const MonitorListCard = CatalogMonitorsListCard;
